import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getLenis } from '@/hooks/useSmoothScroll';

/**
 * Resets scroll position to the top of the page on every route change.
 *
 * Hash links (e.g. /page#section) are honoured — only pathname changes trigger
 * the reset. We fire on the next animation frame so React has committed the
 * new route's mount, and we call both Lenis and the native API: Lenis owns
 * the smooth-scroll loop and ignores stale targets if the document height
 * hasn't been recomputed yet, so the native call is the belt to Lenis's braces.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;

    function reset() {
      const lenis = getLenis();
      if (lenis) {
        // Resize first so Lenis sees the new page's actual height.
        lenis.resize();
        lenis.scrollTo(0, { immediate: true, force: true });
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }

    // Fire after React commits + browser does layout for the new route.
    reset();
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
